/** Match AppContext.round2 — keep util free of context imports (circular dep risk). */
const round2 = (num) => {
  const n = Number(num);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

/**
 * Embed supplier FIXED_PER_PERSON + optional hr_commission_fixed (per person × pax)
 * into ourTotalFare. Replaces % HR / agent for this rule type.
 *
 * Formula (USD): LDS − (supplier_fixed × pax) + (hr_fixed × pax)
 *
 * Mutates `ourFareDetails` in place when applicable.
 *
 * @param {Record<string, unknown>|null|undefined} ourFareDetails
 * @param {{
 *   adults?: number,
 *   kids?: number,
 *   totalFare?: number|string,
 *   currency?: string,
 *   currencyRate?: Record<string, number>|null,
 *   creditCardFeePercent?: number,
 * }} opts
 * @returns {{ applied: boolean, reason?: string }}
 */
export function applyLdsFixedPerPersonToOurFareDetails(ourFareDetails, opts = {}) {
  const cond = ourFareDetails?.ldsCommissionCondition;
  const ct = String(cond?.commission_type || "")
    .trim()
    .toUpperCase();
  if (ct !== "FIXED_PER_PERSON") {
    return { applied: false, reason: "not_fixed_per_person" };
  }

  const otf = ourFareDetails?.ourTotalFare;
  if (!otf || typeof otf !== "object") {
    return { applied: false, reason: "missing_ourTotalFare" };
  }

  const currency = opts.currency;
  const currencyRate = opts.currencyRate || {};
  const dealCurrencyRate = currency ? currencyRate?.[currency] : null;
  const usdRate = currencyRate?.USD;
  if (!dealCurrencyRate || !usdRate) {
    return { applied: false, reason: "missing_currency_rates" };
  }

  const adults = Number(opts.adults) || 0;
  const kids = Number(opts.kids) || 0;
  const numberOfPeople = adults + kids;
  const fixedPp = Number(cond.fixed_commission_per_person || 0);
  const hrFixedPp = Number(cond.hr_commission_fixed || 0);
  const totalFixedUsd = fixedPp * numberOfPeople;
  const totalHrFixedUsd = hrFixedPp * numberOfPeople;

  const totalFare = opts.totalFare;
  let originalLdsPriceUsd;
  if (String(currency) === "USD") {
    originalLdsPriceUsd = round2(parseInt(totalFare, 10) || 0);
  } else {
    const originalLdsPriceIls = round2(
      (parseInt(totalFare, 10) || 0) * dealCurrencyRate
    );
    originalLdsPriceUsd = round2(originalLdsPriceIls / (usdRate || 1));
  }

  const netPriceUsd = round2(originalLdsPriceUsd - totalFixedUsd);
  const finalPriceUsd = round2(netPriceUsd + totalHrFixedUsd);
  const finalPriceIls = round2(finalPriceUsd * usdRate);

  otf.initialPrice = finalPriceIls;
  otf.hrCommission = 0;
  otf.agentCommission = 0;
  otf.hrCommissionPercent = 0;
  otf.agentCommissionPercent = 0;
  otf.priceWithCommission = finalPriceIls;
  otf.hrMarginPricingMode = "fixed_per_person_embedded";
  otf.supplierFixedCommissionTotalUsd = totalFixedUsd;
  otf.hrCommissionFixedUsdApplied = totalHrFixedUsd;
  otf.hrMarginEmbeddedIls = round2(totalHrFixedUsd * usdRate);

  const creditCardFeePercent = Number(opts.creditCardFeePercent) || 0;
  const priceWithCommission = otf.priceWithCommission || 0;
  const creditCardFee = round2(
    (priceWithCommission * creditCardFeePercent) / 100
  );
  otf.creditCardFee = creditCardFee || 0;
  otf.priceWithCommissionAndCreditCardFee =
    round2(priceWithCommission + (creditCardFee || 0)) || priceWithCommission;
  otf.netPrice = round2(netPriceUsd * usdRate);

  if (!otf.originalCurrency) {
    otf.originalCurrency = currency;
  }

  return {
    applied: true,
    netPriceUsd,
    finalPriceUsd,
    finalPriceIls,
    totalFixedUsd,
    totalHrFixedUsd,
    numberOfPeople,
  };
}
